import React from "react";
import {LoaderCircle} from "lucide-react";
import {Badge} from "@/components/ui/badge";
import {Collapsible, CollapsibleContent, CollapsibleTrigger} from "@/components/ui/collapsible";
import {Form, FormControl, FormField, FormItem, FormLabel, FormMessage} from "@/components/ui/form";
import {Input} from "@/components/ui/input";
import {Separator} from "@/components/ui/separator";
import {Button} from "@/components/ui/button";

interface PermutaValutazioneSectionProps {
    formDatiCliente: any;
    eurotaxValutazione?: EurotaxValutazione | null;
    isValutazioneLoading?: boolean;
    isClienteProfileComplete?: boolean;
    isManualValutazioneOverride?: boolean;
    onEnableValutazioneOverride: () => void;
    onDisableValutazioneOverride: () => void;
}

const isDevelopment = process.env.NODE_ENV !== "production";

const formatCurrency = (value: number | null | undefined) => {
    if (typeof value !== "number" || !Number.isFinite(value)) return null;
    return new Intl.NumberFormat("it-IT", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 2,
    }).format(value);
};

const roundDownToHundreds = (value: number | null | undefined) => {
    if (typeof value !== "number" || !Number.isFinite(value)) return null;
    return Math.floor(value / 100) * 100;
};

const formatCurrencyWithoutDecimals = (value: number | null | undefined) => {
    if (typeof value !== "number" || !Number.isFinite(value)) return null;
    return new Intl.NumberFormat("it-IT", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value);
};

const formatPercentage = (value: number | null | undefined) => {
    if (typeof value !== "number" || !Number.isFinite(value)) return null;
    const normalized = Math.abs(value) <= 1 ? value * 100 : value;
    return `${normalized.toFixed(2)}%`;
};

const getPrimaryDisplayValue = (valutazione?: EurotaxValutazione | null) => {
    if (!valutazione) return null;
    if (typeof valutazione.quotazioneProposta !== "number" || !Number.isFinite(valutazione.quotazioneProposta)) {
        return null;
    }
    if (valutazione.quotazioneProposta <= 0) return null;

    return formatCurrencyWithoutDecimals(roundDownToHundreds(valutazione.quotazioneProposta));
};

const renderChipList = (values?: string[]) => {
    if (!values?.length) return null;

    return (
        <div className="flex flex-wrap gap-2">
            {values.map((value) => (
                <Badge key={value} variant="outline" className="rounded-md px-2 py-1 text-[11px] font-medium">
                    {value}
                </Badge>
            ))}
        </div>
    );
};

const PermutaValutazioneSection: React.FC<PermutaValutazioneSectionProps> = ({
    formDatiCliente,
    eurotaxValutazione,
    isValutazioneLoading = false,
    isClienteProfileComplete = false,
    isManualValutazioneOverride = false,
    onEnableValutazioneOverride,
    onDisableValutazioneOverride,
}) => {
    const primaryDisplayValue = getPrimaryDisplayValue(eurotaxValutazione);
    const coefficienteDisplay = formatPercentage(eurotaxValutazione?.coefficienteValutazione);
    const margineDisplay = formatCurrency(eurotaxValutazione?.margineAggiuntivoDesiderato);
    const margineDbDisplay = formatPercentage(eurotaxValutazione?.margineDbDiagnostico);
    const valoreRiferimentoDisplay = formatCurrency(eurotaxValutazione?.valoreRiferimento);
    const streetPriceFallbackDisplay = formatCurrency(eurotaxValutazione?.streetPriceMinimoFallback);
    const showDebugPanel = isDevelopment && !!eurotaxValutazione;
    const shouldShowManualValutazioneInput = isClienteProfileComplete && !isValutazioneLoading && !primaryDisplayValue;

    return (
        <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-4 space-y-4">
            {!shouldShowManualValutazioneInput ? (
                <div className="space-y-1">
                    <p className="text-sm font-medium text-slate-600">Valutazione dell&apos;usato:</p>
                    {!isClienteProfileComplete ? (
                        <>
                            <p className="text-lg font-semibold text-slate-900">Completa i dati cliente</p>
                            <p className="text-sm text-slate-500">
                                Inserisci nome, cognome, telefono, provincia e targa per avviare il calcolo automatico.
                            </p>
                        </>
                    ) : isValutazioneLoading ? (
                        <>
                            <div className="flex items-center gap-3">
                                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-200 text-slate-600">
                                    <LoaderCircle className="h-6 w-6 animate-spin"/>
                                </div>
                                <div className="space-y-1">
                                    <p className="text-lg font-semibold text-slate-950">Sto calcolando il valore della tua auto...</p>
                                    <p className="text-sm text-slate-500">
                                        Analizzo veicolo, coefficienti e dati supplementari disponibili prima di mostrarti l&apos;importo.
                                    </p>
                                </div>
                            </div>
                            <p className="text-sm text-slate-500">Attendi qualche secondo, il risultato arriva in automatico.</p>
                        </>
                    ) : primaryDisplayValue ? (
                        <p className="text-3xl font-semibold tracking-tight text-slate-950">
                            {primaryDisplayValue}
                        </p>
                    ) : null}
                </div>
            ) : null}

            {shouldShowManualValutazioneInput ? (
                <Form {...formDatiCliente}>
                    <FormField control={formDatiCliente.control} name="valutazione"
                               render={({field, fieldState}) => (
                                   <FormItem>
                                       <FormLabel>Quanto vorresti ricavare</FormLabel>
                                       <FormControl>
                                           <Input {...field} placeholder="Inserisci l'importo desiderato"/>
                                       </FormControl>
                                       <FormMessage>{fieldState.error?.message}</FormMessage>
                                   </FormItem>
                               )}
                    />
                </Form>
            ) : null}

            {showDebugPanel && isClienteProfileComplete && !isValutazioneLoading ? (
                <Collapsible>
                    <CollapsibleTrigger asChild>
                        <Button type="button" variant="ghost" className="h-auto px-0 text-xs text-slate-500 hover:bg-transparent hover:text-slate-900">
                            Mostra diagnostica Eurotax
                        </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="space-y-4 pt-2">
                        {(valoreRiferimentoDisplay || coefficienteDisplay || eurotaxValutazione?.fonteCoefficiente || margineDisplay) ? (
                            <>
                                <Separator/>
                                <div className="grid gap-3 md:grid-cols-2">
                                    {valoreRiferimentoDisplay ? (
                                        <div className="space-y-1">
                                            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Base calcolo</p>
                                            <p className="text-sm font-semibold text-slate-900">{valoreRiferimentoDisplay}</p>
                                        </div>
                                    ) : null}
                                    {coefficienteDisplay && eurotaxValutazione?.fonteCoefficiente === "db" ? (
                                        <div className="space-y-1">
                                            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Coefficiente DB</p>
                                            <p className="text-sm font-semibold text-slate-900">{coefficienteDisplay}</p>
                                        </div>
                                    ) : null}
                                    {eurotaxValutazione?.fonteCoefficiente ? (
                                        <div className="space-y-1">
                                            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Origine coefficiente</p>
                                            <p className="text-sm font-semibold text-slate-900">{eurotaxValutazione.fonteCoefficiente}</p>
                                        </div>
                                    ) : null}
                                    {margineDisplay ? (
                                        <div className="space-y-1">
                                            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Margine aggiuntivo</p>
                                            <p className="text-sm font-semibold text-slate-900">{margineDisplay}</p>
                                        </div>
                                    ) : null}
                                </div>
                            </>
                        ) : null}

                        <div className="grid gap-3 md:grid-cols-2">
                            {eurotaxValutazione?.codiceCostruttore ? (
                                <div className="space-y-1">
                                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Codice costruttore Eurotax</p>
                                    <p className="text-sm text-slate-900">{eurotaxValutazione.codiceCostruttore}</p>
                                </div>
                            ) : null}
                            {margineDbDisplay ? (
                                <div className="space-y-1">
                                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Margine DB diagnostico (%)</p>
                                    <p className="text-sm text-slate-900">{margineDbDisplay}</p>
                                </div>
                            ) : null}
                            {streetPriceFallbackDisplay ? (
                                <div className="space-y-1">
                                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Fallback Street Price Min</p>
                                    <p className="text-sm text-slate-900">{streetPriceFallbackDisplay}</p>
                                </div>
                            ) : null}
                            {eurotaxValutazione?.codiciCostruttoreAlternativi?.length ? (
                                <div className="space-y-2">
                                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Codici costruttore alternativi</p>
                                    {renderChipList(eurotaxValutazione.codiciCostruttoreAlternativi)}
                                </div>
                            ) : null}
                            {eurotaxValutazione?.codiciCostruttoreRicerca?.length ? (
                                <div className="space-y-2 md:col-span-2">
                                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Chiavi ricerca DB</p>
                                    {renderChipList(eurotaxValutazione.codiciCostruttoreRicerca)}
                                </div>
                            ) : null}
                        </div>

                        {eurotaxValutazione?.rigaEurotaxDb ? (
                            <details className="rounded-md border border-slate-200 bg-white p-3">
                                <summary className="cursor-pointer text-xs font-medium uppercase tracking-wide text-slate-600">
                                    Riga raw Eurotax DB
                                </summary>
                                <pre className="mt-3 overflow-x-auto rounded-md bg-slate-950 p-3 text-xs text-slate-100">
                                    {JSON.stringify(eurotaxValutazione.rigaEurotaxDb, null, 2)}
                                </pre>
                            </details>
                        ) : null}
                    </CollapsibleContent>
                </Collapsible>
            ) : null}
        </div>
    );
};

export default PermutaValutazioneSection;
